import { motion } from "framer-motion";
import { Dumbbell, HeartPulse, Info, Loader2, Salad } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const TONES = {
  ink: { solid: "bg-ink text-white", soft: "bg-stone-100", text: "text-ink", dot: "bg-ink", border: "border-ink" },
  train: { solid: "bg-train text-white", soft: "bg-train-soft", text: "text-train-ink", dot: "bg-train", border: "border-train" },
  food: { solid: "bg-food text-white", soft: "bg-food-soft", text: "text-food-ink", dot: "bg-food", border: "border-food" },
  care: { solid: "bg-care text-white", soft: "bg-care-soft", text: "text-care-ink", dot: "bg-care", border: "border-care" },
  streak: { solid: "bg-streak text-white", soft: "bg-streak-soft", text: "text-streak-ink", dot: "bg-streak", border: "border-streak" },
};

export const MODULES = {
  training: { label: "Entrenador", tone: "train", icon: Dumbbell, path: "/entrenador", desc: "Plan semanal de ejercicios adaptado a tu nivel y a tus dolencias." },
  nutrition: { label: "Dietista", tone: "food", icon: Salad, path: "/dietista", desc: "Menú diario con calorías, macros y lista de la compra." },
  ailments: { label: "Dolencias", tone: "care", icon: HeartPulse, path: "/dolencias", desc: "Registro de lesiones y dolor, diario de síntomas y consejos." },
};

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

export const Loader = () => (
  <div className="flex items-center justify-center py-24 text-stone-500" data-testid="loading-indicator">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
);

export const PageHeader = ({ eyebrow, title, subtitle, tone = "ink", image, children }) => (
  <Reveal className="relative mb-8 overflow-hidden rounded-3xl">
    {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
    <div className={`relative ${image ? "bg-gradient-to-r from-white via-white/95 to-white/40" : ""} px-1 py-2 ${image ? "p-6 sm:p-8" : ""}`}>
      <p className={`eyebrow ${TONES[tone].text}`}>{eyebrow}</p>
      <h1 className="mt-2 text-3xl font-extrabold text-ink sm:text-4xl" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-2 max-w-xl text-sm text-stone-600 sm:text-base">{subtitle}</p>}
      {children}
    </div>
  </Reveal>
);

export const Chip = ({ active, onClick, children, tone = "ink", testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 active:scale-95 ${
      active ? `${TONES[tone].solid} border-transparent` : "border-stone-200 bg-white text-stone-700 hover:border-stone-400"
    }`}
  >
    {children}
  </button>
);

export const Stat = ({ label, value, unit, testid, tone = "ink" }) => (
  <div className={`rounded-2xl ${TONES[tone].soft} p-4`}>
    <p className="text-xs font-medium text-stone-600">{label}</p>
    <p className={`mt-1 font-display text-2xl font-extrabold ${TONES[tone].text}`} data-testid={testid}>
      {value}
      {unit && <span className="ml-1 text-sm font-semibold">{unit}</span>}
    </p>
  </div>
);

export const Disclaimer = () => (
  <div className="flex gap-3 rounded-2xl border border-care/30 bg-care-soft p-4 text-sm text-care-ink" data-testid="medical-disclaimer">
    <Info className="mt-0.5 h-4 w-4 shrink-0" />
    <p>Esta orientación es general y <strong>no sustituye a un médico ni a un fisioterapeuta</strong>. Ante dolor intenso, persistente o síntomas de alarma, consulta a un profesional sanitario.</p>
  </div>
);

export const ModuleOff = ({ module }) => {
  const { me, refreshMe } = useAuth();
  const m = MODULES[module];
  const activate = async () => {
    try {
      await api.patch("/profile/modules", { ...me.modules, [module]: true });
      await refreshMe();
      toast.success(`Módulo ${m.label} activado`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className={`card-clay mx-auto mt-10 max-w-md p-8 text-center`} data-testid={`module-off-${module}`}>
      <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${TONES[m.tone].soft}`}>
        <m.icon className={`h-7 w-7 ${TONES[m.tone].text}`} />
      </div>
      <h2 className="mt-4 text-xl font-bold">El módulo {m.label} está desactivado</h2>
      <p className="mt-2 text-sm text-stone-600">{m.desc}</p>
      <button onClick={activate} data-testid={`activate-module-${module}`} className={`mt-6 rounded-full px-6 py-3 text-sm font-semibold transition-transform active:scale-95 ${TONES[m.tone].solid}`}>
        Activar {m.label}
      </button>
    </div>
  );
};
